/**
 * AURELIA — component-quick-view.js
 * <quick-view-modal> — fetches and displays a product without leaving the grid.
 *
 * The content is `sections/quick-view.liquid` rendered against the real
 * product URL, so it is the same Liquid the product page uses. Nothing about
 * the product is duplicated in JavaScript.
 *
 * Triggers are any `[data-quick-view-trigger]` with a `data-product-url`.
 * They are delegated from the document, so cards added by infinite scroll or
 * a section re-render work with no re-binding.
 */
(function () {
  'use strict';

  const { Drawer, utils, emit, a11y } = window.Aurelia;

  /**
   * Loads the variant picker the first time a quick view is opened.
   *
   * The picker is only ever needed *inside* this modal, and a script tag
   * inside fetched HTML never executes when that HTML is assigned through
   * innerHTML — so the file has to be requested deliberately. Doing it here
   * rather than in the page head keeps ~2.7 KB off every page of the store
   * for the majority of visitors who never open a quick view.
   *
   * The promise is memoised, so ten rapid clicks make one request.
   */
  let variantPickerReady = null;

  function loadVariantPicker() {
    if (variantPickerReady) return variantPickerReady;

    if (window.customElements.get('variant-picker')) {
      variantPickerReady = Promise.resolve();
      return variantPickerReady;
    }

    const url = document.getElementById('QuickView')?.dataset.variantPickerUrl;
    if (!url) {
      // Nothing to load — the theme is serving a build where the picker is
      // already global. Carrying on is correct.
      variantPickerReady = Promise.resolve();
      return variantPickerReady;
    }

    variantPickerReady = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = url;
      script.defer = true;
      // Resolve either way: a failed picker must not stop the modal opening,
      // and the form still posts without it.
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', resolve, { once: true });
      document.head.appendChild(script);
    });

    return variantPickerReady;
  }

  class QuickViewModal extends Drawer {
    setup() {
      this.target = this.$('[data-quick-view-target]');
      this.skeleton = this.$('[data-quick-view-skeleton]');
      this.cache = new Map();
      this.currentUrl = null;
    }

    onClose() {
      // Keep the last product until the panel has finished sliding out;
      // clearing immediately makes the close animation look broken.
      this.currentUrl = null;
    }

    async show(url, trigger) {
      if (!this.target) return;

      this.currentUrl = url;
      this.open(trigger);

      if (this.cache.has(url)) {
        this.render(this.cache.get(url), url);
        return;
      }

      this.showSkeleton();

      try {
        // Requested alongside the section so the two arrive in parallel rather
        // than one after the other.
        const [html] = await Promise.all([
          window.Aurelia.fetchSection(url, { sectionId: 'quick-view' }),
          loadVariantPicker(),
        ]);
        const content = utils.parseHTML(html).querySelector('[data-quick-view-content]');
        if (!content) throw new Error('Quick view content missing');

        // The variant JSON travels alongside the markup and must come with it.
        const variantData = utils.parseHTML(html).querySelector('[data-variant-json]');
        const fragment = document.createDocumentFragment();
        fragment.appendChild(content);
        if (variantData) fragment.appendChild(variantData);

        const wrapper = document.createElement('div');
        wrapper.appendChild(fragment);

        this.cache.set(url, wrapper.innerHTML);
        this.render(wrapper.innerHTML, url);
      } catch (error) {
        // Falling back to the product page is always better than a dead modal.
        window.location.href = url;
      }
    }

    showSkeleton() {
      if (!this.skeleton) return;
      this.target.innerHTML = '';
      this.target.appendChild(this.skeleton.cloneNode(true));
      this.target.setAttribute('aria-busy', 'true');
    }

    render(html, url) {
      // A slower request for a product the customer has already navigated away
      // from must not overwrite the one they are looking at now.
      if (url !== this.currentUrl) return;

      this.target.innerHTML = html;
      this.target.removeAttribute('aria-busy');

      // Re-trap focus: the element that held it was just replaced.
      const heading = this.target.querySelector('.quick-view__title');
      a11y.trapFocus(this.querySelector('.drawer__panel'), heading || undefined);

      emit('content:appended', { target: this.target });
    }
  }

  window.Aurelia.define('quick-view-modal', QuickViewModal);

  /* ---------------------------------------------------------------------
     Delegated triggers
     --------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-view-trigger]');
    if (!trigger) return;

    const modal = document.getElementById('QuickView');
    const url = trigger.dataset.productUrl;
    if (!modal || !url || typeof modal.show !== 'function') return;

    event.preventDefault();
    trigger.setAttribute('aria-busy', 'true');

    modal.show(url, trigger).finally(() => trigger.removeAttribute('aria-busy'));
  });
})();
